<template>
  <section class="word-break">
    <!-- 國健署資料 --start -->
    <ColonPreviewBowelCleanseForms
      id="colonPreviewBowelCleanseForms"
      v-if="isColonReport"
      :examination="examination"
      :sedatives="sedatives"
    />
    <div class="border-b">
      <gastro-preview-bowel-cleanse-forms
        v-if="!isColonReport"
        :examination="examination"
        :sedatives="sedatives"
      />
    </div>
    <!-- 國健署資料 --end -->

    <!-- 1. Procedure Note + Findings article + global map -->
    <div class="border-b border-b--dashed py-3 report-top-grid">
      <div class="report-top-grid__content">
        <div class="report-top-grid__procedure">
          <h3 class="print-color subtitle-border mb-2">
            {{ $t('common.procedure_note') }}：
          </h3>
          <p v-if="isColonReport" class="report-insertion-endpoint ml-2 mb-1">
            <strong>Insertion to:</strong> {{ insertionEndpointText }}
          </p>
          <p class="ml-2 mb-0">{{ procedureNoteText }}</p>
        </div>

        <div class="report-top-grid__article mt-3">
          <div class="report-inline-heading">
            <h3 class="print-color subtitle-border mb-2">{{ $t('report_preview.findings') }}:</h3>
            <div v-if="editableReportText" class="report-screen-only">
              <slot name="findings-actions" />
            </div>
          </div>
          <slot name="findings-review-notice" />
          <div v-if="editableReportText && findingsPending" class="report-inline-pending pa-3 d-flex align-center">
            <v-progress-circular indeterminate color="primary" class="mr-3" size="24" />
            <span>Findings 報告生成中…</span>
          </div>
          <div v-else-if="getIsGeneratingDescriptiveParagraph" class="pa-3 d-flex align-center">
            <v-progress-circular indeterminate color="primary" class="mr-3" size="24"></v-progress-circular>
            <span>{{ $t('report.generating_descriptive_paragraph') }}</span>
          </div>
          <div v-else-if="editableReportText" class="report-inline-field pa-3">
            <v-textarea
              :value="llmFindingsText || ''"
              auto-grow
              rows="10"
              hide-details
              outline
              single-line
              placeholder="LLM 尚未產生 Findings"
              @input="$emit('update:llmFindingsText', $event)"
            />
          </div>
          <div v-else class="pa-3">
            <template v-if="isColonReport && colonLlmTexts.length">
              <div v-for="(text, idx) in colonLlmTexts" :key="`detail-colon-des-${idx}`">
                <div class="report-verbatim-text">{{ text }}</div>
              </div>
            </template>
            <template v-else-if="!isColonReport && gastroLlmEntries.length">
              <div v-for="entry in gastroLlmEntries" :key="entry[0]" class="mb-3">
                <p v-if="entry[0]" class="font-weight-bold mb-0">{{ entry[0] }}:</p>
                <div class="report-verbatim-text">{{ entry[1] || '-' }}</div>
              </div>
            </template>
            <p v-else class="ml-2 mb-0">{{ $t('common.nil') }}</p>
          </div>
          <div v-if="editableReportText" class="report-print-only report-verbatim-text pa-3">
            {{ llmFindingsText || $t('common.nil') }}
          </div>
        </div>
      </div>

      <div class="report-top-grid__map">
        <Report2AnatomyMap
          v-if="lesionMap"
          :lesionMap="lesionMap"
          :isColon="isColonReport"
        />
        <FindingInfoByOrder
          v-else-if="isColonReport"
          :findings="findings"
          :imgArrange="imgArrange"
          :showHeader="false"
          :showFindingNames="false"
          :showFindingDescriptions="false"
          :showFindingImages="false"
          :showOtherImages="false"
        />
        <FindingInfoByOrgan
          v-else
          :findings="findings"
          :imgArrange="imgArrange"
          :showHeader="false"
          :showFindingNames="false"
          :showFindingDescriptions="false"
          :showFindingImages="false"
          :showOtherImages="false"
        />
      </div>
    </div>

    <!-- 2. Impression -->
    <div class="border-b border-b--dashed">
      <div class="report-inline-heading pt-3">
        <h3 class="print-color subtitle-border mb-2">{{ impressionTitle }}:</h3>
        <div v-if="editableReportText" class="report-screen-only">
          <slot name="impression-actions" />
        </div>
      </div>
      <slot name="impression-review-notice" />
      <!-- EVAS2.0：Impression 由 LLM 抽出的欄位樣板組句（server/service/evas2/
           impression.js），不是結構化 findings —— 由 impressionLines 傳進來。
           不傳（舊版報告工作站）時走原本的 SummaryInfoGroup，行為 1:1。 -->
      <div v-if="editableReportText && impressionPending" class="report-inline-pending px-3 pb-3 d-flex align-center">
        <v-progress-circular indeterminate color="primary" class="mr-3" size="24" />
        <span>Impression 報告生成中…</span>
      </div>
      <div v-else-if="editableReportText" class="report-inline-field px-3 pb-3">
        <v-textarea
          :value="impressionText || ''"
          auto-grow
          rows="5"
          hide-details
          outline
          single-line
          placeholder="抽取欄位中沒有可組句的病灶，請對照逐字稿人工確認"
          @input="$emit('update:impressionText', $event)"
        />
      </div>
      <div v-if="editableReportText" class="report-print-only report-verbatim-text ml-3 mb-2">
        {{ impressionText || $t('common.nil') }}
      </div>
      <div v-else-if="impressionText !== null" class="report-verbatim-text ml-3 mb-2">
        {{ impressionText || $t('common.nil') }}
      </div>
      <ul v-else-if="impressionLines" class="ml-4 mb-0">
        <li v-for="(line, i) in impressionLines" :key="`impression-${i}`">{{ line }}</li>
      </ul>
      <SummaryInfoGroup
        v-else
        type="summary"
        :report-title="reportTitle"
        :findings="findings"
        hide-summary-title
      />
    </div>

    <!-- 3. Suggestion -->
    <div class="border-b border-b--dashed">
      <SummaryInfoGroup
        type="suggestion"
        :report-title="reportTitle"
        :findings="findings"
      />
    </div>

    <!-- 4. Finding descriptions disabled.
    <div class="border-b border-b--dashed py-3">
      <h3 class="print-color subtitle-border mb-2">{{ findingDescriptionsTitle }}:</h3>
      <template v-if="hasFindings">
        <FindingInfoByOrder
          v-if="isColonReport"
          :findings="findings"
          :imgArrange="imgArrange"
          :showHeader="false"
          :showGlobalMap="false"
          :showFindingNames="false"
          :showFindingImages="false"
          :showFindingMap="false"
          :showOtherImages="false"
        />
        <FindingInfoByOrgan
          v-else
          :findings="findings"
          :imgArrange="imgArrange"
          :showHeader="false"
          :showGlobalMap="false"
          :showFindingNames="false"
          :showFindingImages="false"
          :showFindingMap="false"
          :showOtherImages="false"
        />
      </template>
      <p v-else class="pl-3">{{ $t('common.nil') }}</p>
    </div>
    -->

    <!-- 5. Medical images grouped by finding order -->
    <FindingItemImagesGroup
      class="py-3"
      :findings="findingsWithImages"
      :additionImages="[]"
      :imgArrange="imgArrange || 3"
      :title="keyImagesTitle"
      :showMetadata="showImageMetadata"
    >
      <template #title-actions>
        <slot name="key-image-actions" />
      </template>
    </FindingItemImagesGroup>

    <!-- 6. Other images selected for report without finding assignment -->
    <div v-if="additionalImages.length" class="border-t border-t--dashed py-3">
      <h3 class="print-color subtitle-border mb-2">{{ moreImagesTitle }}:</h3>
      <div class="ml-2 break-inside-page" style="padding: 5px">
        <FindingItemImages
          :finding="{ images: additionalImages }"
          :imgArrange="imgArrange || 3"
          :showName="false"
          :showMetadata="showImageMetadata"
        />
      </div>
    </div>

    <div v-if="editableReportText" class="report-screen-only">
      <slot name="after-key-images" />
    </div>
  </section>
</template>

<script>
import { mapGetters } from 'vuex'
import SummaryInfoGroup from '@/components/SummaryInfoGroup.vue'
import GastroPreviewBowelCleanseForms from '@/components/preview/GastroPreviewBowelCleanseForms.vue'
import ColonPreviewBowelCleanseForms from '@/components/preview/ColonPreviewBowelCleanseForms.vue'
import FindingInfoByOrgan from './FindingInfoByOrgan.vue'
import FindingInfoByOrder from './FindingInfoByOrder.vue'
import FindingItemImages from '@/components/preview/FindingItemImages.vue'
import FindingItemImagesGroup from '@/components/preview/FindingItemImagesGroup.vue'
import Report2AnatomyMap from '@/components/report2/Report2AnatomyMap.vue'

import {
  REPORT_IMAGE_CATEGORY,
  reportImageCategory,
  selectedReportImages,
} from '@/utils/reportImageSelection'
import { resolveProcedureNoteText } from '@/utils/procedureNote'
import { reportInsertionEndpoint } from '@/utils/reportInsertion'

export default {
  name: 'SummaryEnReport',
  components: {
    ColonPreviewBowelCleanseForms,
    GastroPreviewBowelCleanseForms,
    SummaryInfoGroup,
    FindingItemImages,
    FindingItemImagesGroup,
    FindingInfoByOrder,
    FindingInfoByOrgan,
    Report2AnatomyMap,
  },
  props: {
    examination: Object,
    sedatives: Array,
    isColonReport: {
      type: Boolean,
      default: true,
    },
    isFullMode: {
      type: Boolean,
      default: true,
    },
    reportTitle: String,
    imgArrange: Number,
    findings: {
      type: Array,
      default: () => [],
    },
    /**
     * EVAS2.0 用：直接指定 Findings 內文（LLM 產生的整段文字）。
     *
     * 舊版報告的 Findings 來自 vuex 的 descriptive paragraph getter；EVAS2.0 的
     * 來源是 examination_transcripts 的 llm_report_edited / llm_report，兩者
     * 落點不同但呈現一樣，所以開一個 prop 讓外面餵進來。**不傳時完全走舊路徑**。
     */
    llmFindingsText: {
      type: String,
      default: null,
    },
    /** EVAS2.0 用：Impression 逐條句子。不傳時走 SummaryInfoGroup（舊版行為）。 */
    impressionLines: {
      type: Array,
      default: null,
    },
    /** EVAS2.0 編修版使用原始文字，保留 textarea 的換行；null 才走舊版資料流。 */
    impressionText: {
      type: String,
      default: null,
    },
    /** EVAS2.0 完整報告編輯頁：在正式段落原位顯示輸入框。 */
    editableReportText: {
      type: Boolean,
      default: false,
    },
    /** EVAS2.0：在正式報告影像下顯示 P 編號、病灶、尺寸與位置。 */
    showImageMetadata: {
      type: Boolean,
      default: false,
    },
    findingsPending: {
      type: Boolean,
      default: false,
    },
    impressionPending: {
      type: Boolean,
      default: false,
    },
    /**
     * EVAS2.0 用：影像分組的病灶清單，形如 `[{ id: 1, name: 'Finding 1' }]`。
     *
     * 為什麼不直接用 `findings` prop：`findings` 同時餵給右側的病灶分布圖
     * （FindingInfoByOrder / FindingInfoByOrgan），那兩個元件吃的是舊版
     * findings / records / options 的結構化物件。把只有 id + name 的簡化物件
     * 塞進去，分布圖會拿不到欄位而爆掉。EVAS2.0 的分布圖目前刻意留空框
     * （findings 傳 []），影像分組另走這一條，兩者互不干擾。
     *
     * 不傳時 fallback 回 `findings`，舊版報告頁行為 1:1 不變。
     */
    imageFindings: {
      type: Array,
      default: null,
    },
    /**
     * EVAS2.0 用：截圖物件上「屬於哪個病灶」的欄位名。
     *
     * 舊版是 `findingID`（指向 findings 資料表）；EVAS2.0 的檢查在 findings 表
     * 裡沒有任何一列，改用 `evas2_finding_no`（LLM 報告裡的第幾段，1-based）。
     * 詳見 server/migrations/20260803120000_evas2_screenshot_finding_no.js。
     */
    imageFindingKey: {
      type: String,
      default: 'findingID',
    },
    /**
     * EVAS2.0：病灶分布圖的資料（utils/lesionMapSpots 的回傳值）。
     *
     * 形狀 `{ spots: number[], groups: [], unplaced: [] }`。
     * 不傳（null）時分布圖走舊路徑——從 `findings` 的 observation 推位置、圈裡
     * 畫序號。EVAS2.0 的 findings 是空陣列，所以舊路徑在這裡等於畫一張空圖，
     * 這個 prop 就是拿來填它的。
     */
    lesionMap: {
      type: Object,
      default: null,
    },
  },
  computed: {
    ...mapGetters('examinations', [
      'getColonDescriptiveParagraph',
      'getGastroDescriptiveParagraph',
      'getIsGeneratingDescriptiveParagraph',
      'getInsertionLevelList'
    ]),
    procedureNoteText() {
      // 沒錄到插入位置時，樣板裡的 (位置) 會連同介系詞整段被移除（不是原樣外洩到
      // 英文正式報告）；note 本身為空則顯示「無」。規則全在 utils/procedureNote.js
      return resolveProcedureNoteText(
        this.examination?.examination?.procedureNote,
        this.examination?.examination?.insertionLevel,
        this.getInsertionLevelList,
        this.$t('common.nil')
      )
    },
    insertionEndpointText() {
      return reportInsertionEndpoint(
        this.examination && this.examination.examination,
        this.getInsertionLevelList
      )
    },
    impressionTitle() {
      return this.$t('report.impression')
    },
    findingDescriptionsTitle() {
      return 'Finding descriptions'
    },
    keyImagesTitle() {
      return this.$t('report.key_images')
    },
    moreImagesTitle() {
      return this.$t('report.more_images')
    },
    hasFindings() {
      return Array.isArray(this.findings) && this.findings.length > 0
    },
    colonLlmTexts() {
      if (this.llmFindingsText !== null) return this.llmFindingsText ? [this.llmFindingsText] : []
      if (Array.isArray(this.getColonDescriptiveParagraph)) {
        return this.getColonDescriptiveParagraph
      }
      if (typeof this.getColonDescriptiveParagraph === 'string' && this.getColonDescriptiveParagraph) {
        return [this.getColonDescriptiveParagraph]
      }
      return []
    },
    gastroLlmEntries() {
      if (this.llmFindingsText !== null) return this.llmFindingsText ? [['', this.llmFindingsText]] : []
      if (this.getGastroDescriptiveParagraph && typeof this.getGastroDescriptiveParagraph === 'object') {
        return Object.entries(this.getGastroDescriptiveParagraph)
      }
      if (typeof this.getGastroDescriptiveParagraph === 'string' && this.getGastroDescriptiveParagraph) {
        return [['', this.getGastroDescriptiveParagraph]]
      }
      return []
    },
    /** 影像分組要依據的病灶清單。EVAS2.0 傳 imageFindings，舊版沿用 findings */
    imageGroupSource() {
      return Array.isArray(this.imageFindings) ? this.imageFindings : this.findings || []
    },
    findingsWithImages() {
      const allImages = selectedReportImages(this.examination?.all)
      const key = this.imageFindingKey
      return this.imageGroupSource.map((finding, idx) => ({
        ...finding,
        // 來源自己指定了標題就用它（EVAS2.0 直接用報告文字裡的 "Finding N"，
        // 影像分組的標題要跟醫師眼前那段文字對得起來，不該被翻譯成「發現1」）
        imageGroupName: finding.imageGroupName || `${this.$t('finding')}${idx + 1}: ${finding.name}`,
        images: allImages
          .filter((item) => (
            reportImageCategory(item, this.imageGroupSource, key) === REPORT_IMAGE_CATEGORY.KEY
            && Number(item[key]) === Number(finding.id)
          ))
          .reverse(),
      }))
    },
    additionalImages() {
      const allImages = selectedReportImages(this.examination?.all)
      const key = this.imageFindingKey
      return allImages
        .filter((item) => (
          reportImageCategory(item, this.imageGroupSource, key) === REPORT_IMAGE_CATEGORY.ADDITIONAL
        ))
        .reverse()
    },
  }
}
</script>

<style lang="scss" scoped>
.report-top-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(320px, auto);
  gap: 24px;
  align-items: start;
}

.report-top-grid__content,
.report-top-grid__map,
.report-top-grid__procedure {
  min-width: 0;
}

.report-verbatim-text {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.report-insertion-endpoint {
  font-size: 14px;
}

.report-inline-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.report-inline-field {
  background: #f8fafc;
  border-radius: 4px;
}

.report-inline-pending {
  min-height: 88px;
  color: #546e7a;
  background: #f5f8fa;
}

.report-print-only {
  display: none;
}

@media print {
  .report-screen-only,
  .report-inline-field {
    display: none !important;
  }

  .report-print-only {
    display: block !important;
  }
}

@media print {
  .report-top-grid {
    grid-template-columns: minmax(0, 1fr) minmax(300px, auto);
    gap: 18px;
  }
}
</style>
